import * as React from 'react';
import {
  DialogTrigger as RACDialogTrigger,
  Modal as RACModal,
  ModalOverlay as RACModalOverlay,
  Dialog as RACDialog,
  Heading,
  type ModalOverlayProps,
} from 'react-aria-components';
import { cva, type VariantProps } from 'class-variance-authority';
import { CloseLine } from '@mingcute/react';

import { cn } from '../lib/cn';

/**
 * Klaret Sheet (handoff §10.8 "Drawer — right-side").
 * Primitive: RAC Modal + ModalOverlay positioned as a right-side panel.
 * Full height, --tvl-r4 on leading (left) corners only, --tvl-e3.
 * Entrance: slide in from right (d-slow).
 * All compound names preserved for existing consumers.
 */

export const SheetTrigger = RACDialogTrigger;

/** @deprecated Portal is implicit in RAC Modal — kept for API compat. */
export const SheetPortal: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <>{children}</>
);

export const SheetClose = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, children, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      'rounded-[var(--tvl-r1)] p-1 text-[var(--tvl-ink-3)]',
      'transition-colors duration-[var(--tvl-d-fast)]',
      'hover:bg-[var(--tvl-wash-hover)] hover:text-[var(--tvl-ink)]',
      className,
    )}
    aria-label={props['aria-label'] ?? 'Close'}
    {...props}
  >
    {children ?? <CloseLine size={20} color="currentColor" aria-hidden />}
  </button>
));
SheetClose.displayName = 'SheetClose';

const sheetVariants = cva(
  [
    'fixed inset-y-0 right-0 z-[var(--tvl-z-modal)] flex flex-col',
    'bg-[var(--tvl-raised)] text-[var(--tvl-ink)]',
    /* r4 on leading (left) corners only */
    'rounded-l-[var(--tvl-r4)] shadow-[var(--tvl-e3)]',
    'border-l border-[var(--tvl-hairline)]',
    'p-6',
    /* entrance: slide from right */
    'entering:animate-[sheet-enter_var(--tvl-d-slow)_var(--tvl-ease-out)_forwards]',
    'exiting:animate-[sheet-exit_var(--tvl-d-slow)_var(--tvl-ease-out)_forwards]',
  ].join(' '),
  {
    variants: {
      size: {
        sm: 'w-[360px]',
        md: 'w-[480px]',
        lg: 'w-[640px]',
      },
    },
    defaultVariants: { size: 'md' },
  },
);

export interface SheetContentProps
  extends Omit<ModalOverlayProps, 'className'>,
    VariantProps<typeof sheetVariants> {
  className?: string;
  hideClose?: boolean;
  children?: React.ReactNode;
}

export const SheetContent = React.forwardRef<HTMLDivElement, SheetContentProps>(
  ({ className, size, hideClose = false, children, ...props }, _ref) => (
    <RACModalOverlay
      className={cn(
        'fixed inset-0 z-[var(--tvl-z-modal)]',
        // --tvl-scrim is a dark dimming overlay. (Was a mix of --tvl-ink, which is
        // near-white in dark mode and LIGHTENED the background instead of dimming it.)
        'bg-[var(--tvl-scrim)]',
        'entering:animate-[overlay-enter_var(--tvl-d-slow)_var(--tvl-ease-out)_forwards]',
        'exiting:animate-[overlay-exit_var(--tvl-d-slow)_var(--tvl-ease-out)_forwards]',
      )}
      {...props}
    >
      <RACModal className={cn(sheetVariants({ size }), className)}>
        <RACDialog className="flex h-full flex-col outline-none">
          {({ close }) => (
            <>
              {children}
              {!hideClose && (
                <button
                  onClick={close}
                  aria-label="Close"
                  className={cn(
                    'absolute right-4 top-4 rounded-[var(--tvl-r1)] p-1',
                    'text-[var(--tvl-ink-3)] transition-colors duration-[var(--tvl-d-fast)]',
                    'hover:bg-[var(--tvl-wash-hover)] hover:text-[var(--tvl-ink)]',
                  )}
                >
                  <CloseLine size={20} color="currentColor" aria-hidden />
                </button>
              )}
            </>
          )}
        </RACDialog>
      </RACModal>
    </RACModalOverlay>
  ),
);
SheetContent.displayName = 'SheetContent';

/**
 * Sheet root. Accepts both Radix-style `open`/`onOpenChange` (legacy)
 * and RAC-style `isOpen`/`onOpenChange` so existing consumers work unchanged.
 */
export interface SheetRootProps extends Omit<React.ComponentProps<typeof RACDialogTrigger>, 'isOpen'> {
  /** Radix compat alias for `isOpen`. */
  open?: boolean;
  isOpen?: boolean;
}

export const Sheet: React.FC<SheetRootProps> = ({ open, isOpen, ...props }) => (
  <RACDialogTrigger isOpen={isOpen ?? open} {...props} />
);

/* ── Structural slots ────────────────────────────────────────────────────── */
export function SheetHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-1.5 text-left', className)} {...props} />;
}

export function SheetFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('mt-auto flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

export const SheetTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <Heading
    ref={ref}
    slot="title"
    className={cn('text-lg font-semibold leading-none text-[var(--tvl-ink)]', className)}
    {...props}
  />
));
SheetTitle.displayName = 'SheetTitle';

export const SheetDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-[var(--tvl-ink-2)]', className)}
    {...props}
  />
));
SheetDescription.displayName = 'SheetDescription';
